// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  PlusCircle,
  Zap,
  Check,
  Info,
  BatteryCharging,
  Droplet,
  Compass,
  HelpCircle,
  CheckCircle,
  Tag,
} from 'lucide-react';
import {
  FurnitureCategory,
  FurnitureDefinition,
  InteractionTypeDefinition,
  MCPToolDefinition,
} from '../types';
import { CustomAssetUploader } from './CustomAssetUploader';
import AppIcon from './AppIcon';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './CreateFurnitureDrawer.css';

export interface FurnitureCategoryItem {
  id: FurnitureCategory;
  name: string;
  icon: string;
}

interface CreateFurnitureDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (furnitureDef: FurnitureDefinition) => void;
  categories: FurnitureCategoryItem[];
  activeCategory: FurnitureCategory;
  activeInteractionTypes: InteractionTypeDefinition[];
  mcpTools: MCPToolDefinition[];
  onAddNewMcpTool: (tool: MCPToolDefinition) => void;
  onOpenInteractionGuide?: () => void;
  onOpenNewInteractionModal?: () => void;
}

// === COMPONENTE: DRAWER DE CREACIÓN DE RECURSOS / MOBILIARIO ===
export const CreateFurnitureDrawer: React.FC<CreateFurnitureDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  categories,
  activeCategory,
  activeInteractionTypes,
  mcpTools,
  onAddNewMcpTool,
  onOpenInteractionGuide,
  onOpenNewInteractionModal,
}) => {
  const drawerRef = useRef<HTMLElement>(null);

  // === ESTADO DEL FORMULARIO ===
  const [name, setName] = useState<string>('');
  const [category, setCategory] = useState<FurnitureCategory>(activeCategory);
  const [width, setWidth] = useState<number>(1);
  const [height, setHeight] = useState<number>(1);
  const [icon, setIcon] = useState<string>('📦');
  const [isWalkable, setIsWalkable] = useState<boolean>(false);
  const [interactionType, setInteractionType] = useState<string>('work');
  const [customImage, setCustomImage] = useState<string | undefined>(undefined);
  const [mcpToolId, setMcpToolId] = useState<string>('none');

  // === CREADOR RÁPIDO DE HERRAMIENTA MCP ===
  const [isCreatingMcpTool, setIsCreatingMcpTool] = useState<boolean>(false);
  const [newMcpName, setNewMcpName] = useState<string>('');
  const [newMcpCategory, setNewMcpCategory] = useState<string>('Messaging');
  const [newMcpIcon, setNewMcpIcon] = useState<string>('⚡');
  const [newMcpDesc, setNewMcpDesc] = useState<string>('');

  // === RESET / SINCRONIZACIÓN AL ABRIR ===
  useEffect(() => {
    if (isOpen) {
      setCategory(activeCategory);
      setMcpToolId('none');
      setIsCreatingMcpTool(false);
      setName('');
      setIcon('📦');
      setWidth(1);
      setHeight(1);
      setIsWalkable(false);
      setInteractionType('work');
      setCustomImage(undefined);
    }
  }, [isOpen, activeCategory]);

  // === CIERRE POR CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }

      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // === GUARDAR NUEVA HERRAMIENTA MCP ===
  const handleSaveMcpTool = () => {
    if (!newMcpName.trim()) return;
    const newId = `mcp-custom-${Date.now()}`;
    const tool: MCPToolDefinition = {
      id: newId,
      name: newMcpName.trim(),
      category: newMcpCategory.trim() || 'General',
      icon: newMcpIcon.trim() || '⚡',
      description: newMcpDesc.trim() || 'Custom MCP tool callable by agents.',
      color: '#f59e0b',
    };
    onAddNewMcpTool(tool);
    setMcpToolId(newId);
    setIsCreatingMcpTool(false);
    setNewMcpName('');
    setNewMcpCategory('Messaging');
    setNewMcpIcon('⚡');
    setNewMcpDesc('');
  };

  // === GUARDAR RECURSO NUEVO ===
  const handleCreate = () => {
    if (!name.trim()) return;
    const newType = `custom_furn_${Date.now()}`;
    const newDef: FurnitureDefinition = {
      type: newType,
      name: name.trim(),
      category,
      width: Math.max(1, Math.min(8, width)),
      height: Math.max(1, Math.min(8, height)),
      icon: icon.trim() || '📦',
      description: 'Custom resource element.',
      isWalkable,
      color: '#3b82f6',
      interactionType: (interactionType || 'work') as any,
      customImage,
      mcpToolId: mcpToolId && mcpToolId !== 'none' ? mcpToolId : undefined,
    };
    onSave(newDef);
    onClose();
  };

  const selectedIntDef = activeInteractionTypes.find((i) => i.id === interactionType);

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="create-furniture-drawer"
      aria-label="Side Drawer: Create New Resource"
      className={`twin-drawer twin-drawer--lg ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}
    >
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-drawer-icon-badge twin-furniture-icon-badge">
            {icon || '📦'}
          </div>
          <div className="twin-drawer-title">
            <span>Create Custom Resource</span>
          </div>
        </div>
        <button type="button" onClick={onClose} className="twin-drawer-close-btn" title="Close drawer (Esc)">
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === CUERPO === */}
      <div className="twin-drawer-body">
        {/* === NOMBRE DEL RECURSO === */}
        {/* === TARJETA: IDENTIFICACIÓN Y DIMENSIONES === */}
        <div className="twin-drawer-card">
          <div className="twin-section-header">
            <Tag className="twin-icon-xs" />
            <h3 className="twin-section-heading">1. Identification & Sizing</h3>
          </div>

          {/* === NOMBRE DEL RECURSO === */}
          <div>
            <label className="twin-field-label">Resource Name</label>
            <input
              type="text"
              placeholder="e.g. Ping Pong Table, Retro Arcade, Statue..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="twin-input twin-input--furniture"
            />
          </div>

        {/* === CATEGORÍA E ÍCONO === */}
        <div className="twin-grid-3">
          <div className="twin-col-span-2">
            <label className="twin-field-label">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as FurnitureCategory)}
              className="twin-select twin-input--furniture"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.icon} {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="twin-field-label">Icon / Emoji</label>
            <input
              type="text"
              placeholder="📦"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="twin-input twin-input--furniture twin-text-center"
            />
          </div>
        </div>

        {/* === DIMENSIONES EN GRID === */}
        <div>
          <label className="twin-field-label">Grid Size (Width x Length in tiles)</label>
          <div className="twin-dimensions-row">
            <div className="twin-dimensions-field">
              <span className="twin-dimensions-hint">Width:</span>
              <input
                type="number"
                min={1}
                max={6}
                value={width}
                onChange={(e) => setWidth(parseInt(e.target.value, 10) || 1)}
                className="twin-dimensions-input twin-input--furniture-value"
              />
            </div>
            <span className="twin-dimensions-x">x</span>
            <div className="twin-dimensions-field">
              <span className="twin-dimensions-hint">Length:</span>
              <input
                type="number"
                min={1}
                max={6}
                value={height}
                onChange={(e) => setHeight(parseInt(e.target.value, 10) || 1)}
                className="twin-dimensions-input twin-input--furniture-value"
              />
            </div>
          </div>
        </div>
      </div>

        {/* === TIPO DE INTERACCIÓN Y CAMINABLE === */}
        <div className="twin-section-gap">
          <div className="twin-section-header twin-row-between">
            <div className="twin-inline-icon-label">
              <Zap className="twin-icon-xs" />
              <h3 className="twin-section-heading twin-no-margin">Interaction Type</h3>
            </div>
            <div className="twin-header-actions">
              {onOpenInteractionGuide && (
                <button type="button" onClick={onOpenInteractionGuide} className="twin-link-btn">
                  <HelpCircle className="twin-icon-xs" />
                  Guide & Help
                </button>
              )}
              {onOpenNewInteractionModal && (
                <button type="button" onClick={onOpenNewInteractionModal} className="twin-link-btn twin-link-btn--accent">
                  <Plus className="twin-icon-xs" />
                  New Interaction
                </button>
              )}
            </div>
          </div>

          <div className="twin-grid-3">
            <div className="twin-col-span-2">
              <select
                value={interactionType}
                onChange={(e) => {
                  const val = e.target.value;
                  setInteractionType(val);
                  const matched = activeInteractionTypes.find((i) => i.id === val);
                  if (matched && matched.behavior.isWalkableDefault !== undefined) {
                    setIsWalkable(matched.behavior.isWalkableDefault);
                  }
                }}
                className="twin-select twin-input--furniture"
              >
                <optgroup label="Available Interactions">
                  {activeInteractionTypes.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name} ({it.category})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="No Interaction">
                  <option value="none">None (Decorative / Structural Resource)</option>
                </optgroup>
              </select>
            </div>

            <label className="twin-checkbox-row" title="Walkable: agents can walk through this tile without path obstruction">
              <input type="checkbox" checked={isWalkable} onChange={(e) => setIsWalkable(e.target.checked)} />
              <div className="twin-checkbox-text">
                <span className="twin-checkbox-title">Walkable?</span>
                <span className="twin-checkbox-hint">{isWalkable ? 'Pass-through' : 'Blocks'}</span>
              </div>
            </label>
          </div>

          {/* === VISTA PREVIA DE LA INTERACCIÓN SELECCIONADA === */}
          {selectedIntDef ? (
            <div className="twin-preview-card twin-preview-card--furniture">
              <div className="twin-row-between">
                <span className="twin-preview-title twin-preview-title--label">
                  <AppIcon icon={selectedIntDef.icon} category={selectedIntDef.category} className="twin-icon-sm twin-furniture-accent-icon" />
                  <span>{selectedIntDef.name}</span>
                </span>
                <span className="twin-preview-duration twin-furniture-duration">
                  {selectedIntDef.behavior.durationSec}s duration
                </span>
              </div>
              <p className="twin-preview-desc">{selectedIntDef.description}</p>
              <div className="twin-preview-stats">
                <span className="twin-preview-stat">
                  <BatteryCharging className="twin-icon-xs twin-furniture-accent-icon" />
                  Energy:{' '}
                  <strong className={selectedIntDef.behavior.energyDelta > 0 ? 'twin-stat-positive' : ''}>
                    {selectedIntDef.behavior.energyDelta > 0 ? `+${selectedIntDef.behavior.energyDelta}` : selectedIntDef.behavior.energyDelta}/s
                  </strong>
                </span>
                <span className="twin-preview-stat">
                  <Droplet className="twin-icon-xs twin-preview-icon-cyan" />
                  Restroom:{' '}
                  <strong className={selectedIntDef.behavior.bladderDelta < 0 ? 'twin-stat-positive' : ''}>
                    {selectedIntDef.behavior.bladderDelta > 0 ? `+${selectedIntDef.behavior.bladderDelta}` : selectedIntDef.behavior.bladderDelta}/s
                  </strong>
                </span>
                <span className="twin-preview-stat">
                  <Compass className="twin-icon-xs twin-preview-icon-purple" />
                  Focus: <strong className="twin-preview-icon-purple">+{selectedIntDef.behavior.focusDelta}/s</strong>
                </span>
              </div>
            </div>
          ) : (
            <div className="twin-hint-box">
              <Info className="twin-icon-sm twin-hint-icon" />
              <span>Passive resource: does not alter vital stats or trigger autonomous agent actions.</span>
            </div>
          )}
        </div>
      

        {/* === HERRAMIENTA MCP ASOCIADA === */}
        <div className="twin-drawer-card">
          <div className="twin-row-between">
            <div className="twin-inline-icon-label">
              <Zap className="twin-icon-xs " />
              <h3 className="twin-section-heading twin-no-margin">Associated MCP Tool</h3>
            </div>
            <button type="button" onClick={() => setIsCreatingMcpTool(!isCreatingMcpTool)} className="twin-link-btn twin-link-btn--accent">
              <Plus className="twin-icon-xs" />
              {isCreatingMcpTool ? 'Cancel' : 'Create MCP Tool'}
            </button>
          </div>

          <p className="twin-drawer-card-hint">
            Link an MCP Tool from the existing list or create a new one. The same MCP Tool can be linked to multiple resources.
          </p>

          {isCreatingMcpTool ? (
            <div className="twin-mcp-creator">
              <div className="twin-mcp-creator-header">
                <span className="twin-mcp-creator-title">
                  <PlusCircle className="twin-icon-xs twin-furniture-accent-icon" />
                  <span>New MCP Tool</span>
                </span>
              </div>
              <div className="twin-grid-3">
                <div className="twin-col-span-2">
                  <label className="twin-field-label twin-field-label--sm">MCP Tool Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Generate Invoice, Send SMS..."
                    value={newMcpName}
                    onChange={(e) => setNewMcpName(e.target.value)}
                    className="twin-input twin-input--sm twin-input--furniture"
                  />
                </div>
                <div>
                  <label className="twin-field-label twin-field-label--sm">Icon / Identifier</label>
                  <input
                    type="text"
                    placeholder="zap"
                    value={newMcpIcon}
                    onChange={(e) => setNewMcpIcon(e.target.value)}
                    className="twin-input twin-input--sm twin-input--furniture twin-text-center"
                  />
                </div>
              </div>
              <div className="twin-grid-2">
                <div>
                  <label className="twin-field-label twin-field-label--sm">Category</label>
                  <input
                    type="text"
                    placeholder="e.g. Messaging, Operations..."
                    value={newMcpCategory}
                    onChange={(e) => setNewMcpCategory(e.target.value)}
                    className="twin-input twin-input--sm twin-input--furniture"
                  />
                </div>
                <div>
                  <label className="twin-field-label twin-field-label--sm">Description</label>
                  <input
                    type="text"
                    placeholder="Action details..."
                    value={newMcpDesc}
                    onChange={(e) => setNewMcpDesc(e.target.value)}
                    className="twin-input twin-input--sm twin-input--furniture"
                  />
                </div>
              </div>
              <div className="twin-mcp-creator-actions">
                <button type="button" onClick={() => setIsCreatingMcpTool(false)} className="twin-link-btn">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveMcpTool}
                  disabled={!newMcpName.trim()}
                  className="twin-btn twin-btn--furniture-primary twin-btn--sm"
                >
                  Save MCP Tool
                </button>
              </div>
            </div>
          ) : (
            <select value={mcpToolId} onChange={(e) => setMcpToolId(e.target.value)} className="twin-select twin-input--furniture">
              <option value="none">None (No MCP tool)</option>
              <optgroup label="Available MCP Tools">
                {mcpTools.map((tool) => (
                  <option key={tool.id} value={tool.id}>
                    {tool.name} ({tool.category || 'General'})
                  </option>
                ))}
              </optgroup>
            </select>
          )}

          {/* === VISTA PREVIA DE LA HERRAMIENTA MCP SELECCIONADA === */}
          {(() => {
            if (mcpToolId === 'none') {
              return (
                <div className="twin-info-line">
                  <Info className="twin-icon-sm twin-hint-icon" />
                  <span>No MCP tool linked. You can select one at any time.</span>
                </div>
              );
            }
            const selectedTool = mcpTools.find((t) => t.id === mcpToolId);
            if (!selectedTool) return null;
            return (
              <div className="twin-preview-card twin-preview-card--furniture">
                <div className="twin-row-between">
                  <span className="twin-preview-title twin-furniture-accent-text">
                    <Zap className="twin-icon-sm twin-furniture-accent-icon" />
                    <span>{selectedTool.name}</span>
                  </span>
                  <span className="twin-mcp-category-badge">{selectedTool.category || 'MCP Tool'}</span>
                </div>
                <p className="twin-preview-desc">{selectedTool.description}</p>
                <div className="twin-mcp-footnote">
                  <CheckCircle className="twin-icon-xs" />
                  <span>Existing MCP Tools can be linked to more than one item.</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* === IMAGEN / SPRITE PERSONALIZADO === */}
        <div>
          <label className="twin-field-label">Resource Image (Optional)</label>
          <CustomAssetUploader
            title="Resource Image / Sprite"
            subtitle="Upload custom graphic for this resource"
            currentImage={customImage}
            onImageChange={(url) => setCustomImage(url)}
            defaultIcon={icon || 'box'}
          />
        </div>
      </div>

      {/* === FOOTER === */}
   <div className="twin-drawer-footer twin-drawer-footer--stacked">
      <div className="twin-footer-hint">
        <Info className="twin-icon-sm twin-furniture-accent-icon" />
        <span>Resource will be available in the active catalog.</span>
      </div>

      {/*<div className="twin-footer-divider" />*/}

      <div className="twin-footer-actions">
        <button type="button" onClick={onClose} className="twin-btn-cancel">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={!name.trim()}
          className="twin-btn-save"
        >
          <Check className="twin-icon-sm" />
          <span>Create Resource</span>
        </button>
      </div>
    </div>
    </aside>
  );
};